import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { selectApiUrls } from '../../../redux/slices/apiUrlSlice';
import { createSalesEndpoints } from '../api/salesApi';
import { normalizeCompany } from './CompanyList';
import {
  compareRows,
  fallbackStates,
  getPagedRows,
  getPageSummary,
  getSortLabel,
  normalizeLookupName,
  toArray,
} from './salesPageUtils';

const emptyContactForm = {
  id: 0,
  firstName: '',
  lastName: '',
  email: '',
  companyId: 0,
  phoneNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  country: 'United States',
  deleted: false,
  isActive: true,
};

const normalizeContact = (contact = {}) => ({
  id: Number(contact.id ?? contact.Id ?? 0),
  firstName: String(contact.firstName ?? contact.FirstName ?? ''),
  lastName: String(contact.lastName ?? contact.LastName ?? ''),
  email: String(contact.email ?? contact.Email ?? ''),
  companyId: Number(
    contact.companyId ??
      contact.CompanyId ??
      contact.company?.id ??
      contact.company?.Id ??
      contact.Company?.id ??
      contact.Company?.Id ??
      0
  ),
  company: contact.company || contact.Company
    ? {
        id: Number(contact.company?.id ?? contact.company?.Id ?? contact.Company?.id ?? contact.Company?.Id ?? contact.companyId ?? contact.CompanyId ?? 0),
        name: String(contact.company?.name ?? contact.company?.Name ?? contact.Company?.name ?? contact.Company?.Name ?? ''),
      }
    : undefined,
  phoneNumber: String(contact.phoneNumber ?? contact.PhoneNumber ?? contact.contactNumber ?? contact.ContactNumber ?? ''),
  streetAddress: String(contact.streetAddress ?? contact.StreetAddress ?? ''),
  city: String(contact.city ?? contact.City ?? ''),
  state: String(contact.state ?? contact.State ?? ''),
  country: String(contact.country ?? contact.Country ?? 'United States'),
  deleted: Boolean(contact.deleted ?? contact.Deleted ?? false),
  isActive: Boolean(contact.isActive ?? contact.IsActive ?? true),
});

const withCompany = (contact, companyOptions) => {
  if (contact.company?.id && contact.company?.name) return contact;
  const matchedCompany = companyOptions.find((company) => company.id === Number(contact.companyId));
  return matchedCompany ? { ...contact, company: matchedCompany } : contact;
};

const ContactList = () => {
  const apiUrls = useSelector(selectApiUrls);
  const endpoints = useMemo(() => createSalesEndpoints(apiUrls), [apiUrls]);
  const portalApiRequest = usePortalApiRequest();
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');

  const [contacts, setContacts] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState(fallbackStates);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [contactFormError, setContactFormError] = useState('');
  const [savingContactForm, setSavingContactForm] = useState(false);
  const [contactPendingDelete, setContactPendingDelete] = useState(null);
  const [deletingContact, setDeletingContact] = useState(false);
  const [updatingActiveContactId, setUpdatingActiveContactId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadContacts = async () => {
      setLoading(true);
      setPageError('');

      try {
        const response = await portalApiRequest({ method: 'GET', url: endpoints.contacts.base });
        if (isMounted) {
          setContacts(toArray(response).map(normalizeContact));
        }
      } catch {
        if (isMounted) {
          setContacts([]);
          setPageError('Unable to load contacts.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadContacts();

    return () => {
      isMounted = false;
    };
  }, [endpoints, portalApiRequest]);

  useEffect(() => {
    let isMounted = true;

    const loadOptions = async () => {
      try {
        const [companiesResponse, statesResponse] = await Promise.all([
          portalApiRequest({ method: 'GET', url: endpoints.companies.base }).catch(() => []),
          portalApiRequest({ method: 'GET', url: endpoints.lookups.byTypeName('States') }).catch(() => []),
        ]);

        const companies = toArray(companiesResponse).map(normalizeCompany).filter((company) => company.id && company.name);
        const states = toArray(statesResponse).map(normalizeLookupName).filter(Boolean);

        if (isMounted) {
          if (companies.length > 0) setCompanyOptions(companies.map(({ id, name }) => ({ id, name })));
          if (states.length > 0) setStateOptions(states);
        }
      } catch {
        // Keep fallback values.
      }
    };

    loadOptions();

    return () => {
      isMounted = false;
    };
  }, [endpoints, portalApiRequest]);

  useEffect(() => {
    if (companyOptions.length > 0) {
      setContacts((current) => current.map((contact) => withCompany(contact, companyOptions)));
    }
  }, [companyOptions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, searchTerm]);

  const filteredContacts = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return contacts;

    return contacts.filter((contact) =>
      [contact.firstName, contact.lastName, contact.email, contact.company?.name, contact.phoneNumber, contact.country]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [contacts, searchTerm]);

  const sortedContacts = useMemo(
    () => [...filteredContacts].sort((left, right) => compareRows(left, right, sortField, sortDirection)),
    [filteredContacts, sortDirection, sortField]
  );

  const pagedContacts = getPagedRows(sortedContacts, currentPage, rowsPerPage);
  const { firstRecord, lastRecord, totalPages } = getPageSummary(sortedContacts.length, currentPage, rowsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const openContactModal = (contact) => {
    if (!canEditSales) return;
    setContactForm(contact?.id ? normalizeContact(contact) : { ...emptyContactForm });
    setContactFormError('');
    setShowContactModal(true);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setContactForm({ ...emptyContactForm });
    setContactFormError('');
    setSavingContactForm(false);
  };

  const onContactFormFieldChange = (field, value) => {
    setContactForm((current) => ({ ...current, [field]: value }));
  };

  const validateContactForm = () => {
    if (!contactForm.firstName.trim()) return 'Please enter first name.';
    if (contactForm.firstName.trim().length < 3) return 'First name must be longer than 3 characters.';
    if (!contactForm.lastName.trim()) return 'Please enter last name.';
    if (contactForm.lastName.trim().length < 3) return 'Last name must be longer than 3 characters.';
    if (!contactForm.companyId || Number(contactForm.companyId) <= 0) return 'Please select company.';
    if (!contactForm.email.trim()) return 'Please enter email address.';
    if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(contactForm.email.trim())) return 'Invalid email address.';
    if (!contactForm.phoneNumber.trim()) return 'Please enter phone number.';
    if (!/^[+ ]?(\d{3}[- ]?){2}\d{4}$/.test(contactForm.phoneNumber.trim())) return 'Phone number format: 123-123-1234.';
    return '';
  };

  const saveContactForm = async () => {
    if (!canEditSales) return;

    const validationError = validateContactForm();
    if (validationError) {
      setContactFormError(validationError);
      return;
    }

    const selectedCompany = companyOptions.find((company) => company.id === Number(contactForm.companyId));
    const payload = normalizeContact({
      ...contactForm,
      companyId: Number(contactForm.companyId),
      company: selectedCompany,
    });

    setSavingContactForm(true);
    setContactFormError('');

    try {
      if (payload.id) {
        const response = await portalApiRequest({
          method: 'PUT',
          url: endpoints.contacts.byId(payload.id),
          data: payload,
        });
        const updatedContact = normalizeContact({ ...payload, ...(response?.data ?? response ?? {}) });
        setContacts((current) => current.map((contact) => (contact.id === updatedContact.id ? updatedContact : contact)));
      } else {
        const response = await portalApiRequest({
          method: 'POST',
          url: endpoints.contacts.base,
          data: payload,
        });
        const createdContact = normalizeContact(response?.data ?? response ?? payload);
        setContacts((current) => [createdContact, ...current]);
      }
      closeContactModal();
    } catch {
      setContactFormError('Unable to save contact.');
    } finally {
      setSavingContactForm(false);
    }
  };

  const updateContactActive = async (contact, nextActive) => {
    if (!canEditSales) return;

    const previousValue = Boolean(contact.isActive);
    setUpdatingActiveContactId(contact.id);
    setContacts((current) => current.map((item) => (item.id === contact.id ? { ...item, isActive: nextActive } : item)));

    try {
      await portalApiRequest({
        method: 'PUT',
        url: endpoints.contacts.byId(contact.id),
        data: { ...contact, isActive: nextActive },
      });
    } catch {
      setContacts((current) => current.map((item) => (item.id === contact.id ? { ...item, isActive: previousValue } : item)));
      setPageError('Unable to update contact.');
    } finally {
      setUpdatingActiveContactId(null);
    }
  };

  const removeContact = async () => {
    if (!canEditSales || !contactPendingDelete?.id) return;

    const contactId = contactPendingDelete.id;
    setDeletingContact(true);

    try {
      await portalApiRequest({ method: 'DELETE', url: endpoints.contacts.byId(contactId) });
      setContacts((current) => current.filter((contact) => contact.id !== contactId));
      setContactPendingDelete(null);
    } catch {
      setPageError('Unable to delete contact.');
    } finally {
      setDeletingContact(false);
    }
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-contacts-title">
      <Row className="mb-3">
        <Col>
          <h4 id="sales-contacts-title">Contact List</h4>
        </Col>
      </Row>

      {!canEditSales && <Alert variant="info">You have read-only access to Sales contacts.</Alert>}
      {pageError && <Alert variant="danger">{pageError}</Alert>}

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i
                className="bi bi-search position-absolute"
                style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}
              ></i>
              <Form.Control
                id="sales-contact-search"
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search"
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          {canEditSales && (
            <Button type="button" className="btn-success addbtnview" onClick={() => openContactModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add New Contact
            </Button>
          )}
        </Col>
      </Row>

      {loading ? (
        <div className="sales-loading"><Spinner animation="border" /></div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  {[
                    ['firstName', 'First Name'],
                    ['lastName', 'Last Name'],
                    ['email', 'Email'],
                    ['company.name', 'Company'],
                    ['phoneNumber', 'Contact Number'],
                  ].map(([field, label]) => (
                    <th key={field}>
                      <Button type="button" variant="link" className="sales-sort-button" onClick={() => handleSort(field)}>
                        {label} <span>{getSortLabel(sortField, sortDirection, field)}</span>
                      </Button>
                    </th>
                  ))}
                  <th>Active</th>
                  {canEditSales && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {pagedContacts.length === 0 ? (
                  <tr>
                    <td colSpan={canEditSales ? 7 : 6} style={{ textAlign: 'left' }}>No contacts found.</td>
                  </tr>
                ) : (
                  pagedContacts.map((contact) => (
                    <tr key={contact.id || `${contact.email}-${contact.firstName}`}>
                      <td>{contact.firstName}</td>
                      <td>{contact.lastName}</td>
                      <td>{contact.email}</td>
                      <td>{contact.company?.name || ''}</td>
                      <td>{contact.phoneNumber}</td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={Boolean(contact.isActive)}
                          disabled={!canEditSales || updatingActiveContactId === contact.id}
                          onChange={(event) => updateContactActive(contact, event.target.checked)}
                          aria-label={`Set ${contact.firstName} ${contact.lastName} active`}
                        />
                      </td>
                      {canEditSales && (
                        <td style={{ textAlign: 'center' }}>
                          <div className="d-inline-flex align-items-center" style={{ gap: '10px' }}>
                            <button
                              type="button"
                              className="edit-icon"
                              title="Edit"
                              onClick={(event) => {
                                event.preventDefault();
                                openContactModal(contact);
                              }}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              type="button"
                              className="edit-icon"
                              title="Delete"
                              onClick={(event) => {
                                event.preventDefault();
                                setContactPendingDelete(contact);
                              }}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          </div>
          <div className="sales-pagination d-flex justify-content-between align-items-center p-3 border-top">
            <div>Showing {firstRecord} to {lastRecord} of {sortedContacts.length} entries</div>
            <div className="d-flex align-items-center">
              <Form.Select
                size="sm"
                className="me-2"
                style={{ width: '90px' }}
                value={rowsPerPage}
                onChange={(event) => setRowsPerPage(Number(event.target.value))}
              >
                {[10, 25, 50].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
              <Button size="sm" variant="outline-secondary" className="me-2" disabled={currentPage <= 1} onClick={() => setCurrentPage((page) => page - 1)}>
                Prev
              </Button>
              <span className="me-2">Page {currentPage} of {totalPages}</span>
              <Button size="sm" variant="outline-secondary" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((page) => page + 1)}>
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal show={showContactModal} onHide={closeContactModal} backdrop="static" size="lg">
        <Modal.Header closeButton={!savingContactForm}>
          <Modal.Title>{contactForm.id ? 'Edit Contact' : 'Add Contact'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {contactFormError && <Alert variant="danger">{contactFormError}</Alert>}
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>First Name *</Form.Label>
              <Form.Control value={contactForm.firstName} onChange={(event) => onContactFormFieldChange('firstName', event.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Last Name *</Form.Label>
              <Form.Control value={contactForm.lastName} onChange={(event) => onContactFormFieldChange('lastName', event.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Company *</Form.Label>
              <Form.Select value={contactForm.companyId} onChange={(event) => onContactFormFieldChange('companyId', Number(event.target.value))}>
                <option value={0}>Select Company</option>
                {companyOptions.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Email *</Form.Label>
              <Form.Control type="email" value={contactForm.email} onChange={(event) => onContactFormFieldChange('email', event.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Contact Number *</Form.Label>
              <Form.Control value={contactForm.phoneNumber} onChange={(event) => onContactFormFieldChange('phoneNumber', event.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Street Address</Form.Label>
              <Form.Control value={contactForm.streetAddress} onChange={(event) => onContactFormFieldChange('streetAddress', event.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label>City</Form.Label>
              <Form.Control value={contactForm.city} onChange={(event) => onContactFormFieldChange('city', event.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label>State</Form.Label>
              <Form.Select value={contactForm.state} onChange={(event) => onContactFormFieldChange('state', event.target.value)}>
                <option value="">Select State</option>
                {stateOptions.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Country</Form.Label>
              <Form.Control value={contactForm.country} onChange={(event) => onContactFormFieldChange('country', event.target.value)} />
            </Col>
            <Col md={12}>
              <Form.Check
                type="switch"
                label="Active"
                checked={Boolean(contactForm.isActive)}
                onChange={(event) => onContactFormFieldChange('isActive', event.target.checked)}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeContactModal} disabled={savingContactForm}>Cancel</Button>
          <Button variant="primary" onClick={saveContactForm} disabled={savingContactForm}>
            {savingContactForm ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(contactPendingDelete)} onHide={() => setContactPendingDelete(null)} backdrop="static" centered>
        <Modal.Header closeButton={!deletingContact}>
          <Modal.Title>Delete Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete {[contactPendingDelete?.firstName, contactPendingDelete?.lastName].filter(Boolean).join(' ') || 'this contact'}?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setContactPendingDelete(null)} disabled={deletingContact}>Cancel</Button>
          <Button variant="danger" onClick={removeContact} disabled={deletingContact}>
            {deletingContact ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default ContactList;
